import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  Currency,
  DateRange,
  extractDateRange,
  extractNumber,
  extractNumberArr,
  HxReportService,
  HxStoreService,
  isoDate,
  RfmAnalysisModel,
  toFixed,
  toRouterQueryParams
} from 'hx-services';
import { Subscription } from 'rxjs';
import { subDays, subMonths } from 'date-fns';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ToastrService } from 'ngx-toastr';
import { CurrencyPipe, DatePipe, DecimalPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { HxDateRangeSelectComponent, HxLoaderComponent, HxStoreSelectComponent } from 'hx-component';
import { FormsModule } from '@angular/forms';

export interface RfmData {
  name: string;
  segments: string[];
  clientAmount?: number;
  spendMoneyTotal?: number;
  clientAvgTotal?: number;
  orderTotalAmount?: number;
  clientOrderAvgTotal?: number;
}

@Component({
    selector: 'app-rfm-analysis',
    templateUrl: './rfm-analysis.component.html',
    styleUrls: ['./rfm-analysis.component.css'],
    imports: [NgIf, HxLoaderComponent, HxStoreSelectComponent, FormsModule, HxDateRangeSelectComponent, NgClass, NgFor, RouterLink, DecimalPipe, CurrencyPipe, DatePipe, TranslocoPipe]
})
export class RfmAnalysisComponent implements OnInit, OnDestroy {
  isloading = {
    rfm: false,
  };
  params: {
    date?: DateRange,
    maxFrequency?: number,
    maxMonetary?: number,
    storeIds?: number[],
  }= {};
  list: RfmAnalysisModel[] = [];
  recencySegment1Count?: number;
  recencySegment2Count?: number;
  recencySegment3Count?: number;
  frequencySegment1Count?: number;
  frequencySegment2Count?: number;
  frequencySegment3Count?: number;
  monetarySegment1Count?: number;
  monetarySegment2Count?: number;
  monetarySegment3Count?: number;
  recency40?: string;
  recency90?: string;
  frequency40?: number;
  frequency90?: number;
  monetary33?: number;
  monetary66?: number;
  currency?: Currency;
  rfmDataList: RfmData[] = [];
  isMarketing = false;
  private sub!: Subscription;

  constructor(
    private reportService: HxReportService,
    private storeService: HxStoreService,
    private aRoute: ActivatedRoute,
    private router: Router,
    private tr: TranslocoService,
    private toastr: ToastrService,
  ) {
  }

  async ngOnInit() {
    const response = await this.storeService.isCurrentUserInMarketingDepartment();
    if (!response.result) {
      this.params.maxMonetary = 10000;
      this.params.maxFrequency = 10;
      this.params.date = {from: isoDate(subDays(subMonths(new Date(), 6), 1)), to: isoDate(subDays(new Date(), 1))};
    }
    this.isMarketing = response.result;
    this.sub = this.aRoute.queryParamMap.subscribe(async paramMap => {
      this.params.date = extractDateRange('date', paramMap) ?? {from: isoDate(subDays(subMonths(new Date(), 6), 1)), to: isoDate(subDays(new Date(), 1))};
      this.params.maxMonetary = extractNumber('maxMonetary', paramMap);
      this.params.maxFrequency = extractNumber('maxFrequency', paramMap);
      this.params.storeIds = extractNumberArr('storeIds', paramMap);

      this.load();
    });
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  private navigate() {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams(this.params),
      relativeTo: this.aRoute
    });
  }

  getTypeFilter(): void {
    this.navigate();
  }

  private async load() {
    this.rfmDataList = [];
    this.isloading.rfm = true;
    try {
      this.list = await this.reportService.getRfmAnalysis({
        fromDate: this.params.date.from,
        toDate: this.params.date.to,
        storeIds: this.params.storeIds,
        maxFrequency: this.params.maxFrequency,
        maxMonetary: this.params.maxMonetary,
      });
      this.recencySegment1Count = this.list.filter(r => r.recencySegment === 1).reduce((a, b) => a + b.recencyCount, 0);
      this.recencySegment2Count = this.list.filter(r => r.recencySegment === 2).reduce((a, b) => a + b.recencyCount, 0);
      this.recencySegment3Count = this.list.filter(r => r.recencySegment === 3).reduce((a, b) => a + b.recencyCount, 0);

      this.frequencySegment1Count = this.list.filter(r => r.frequencySegment === 1).reduce((a, b) => a + b.frequencyCount, 0);
      this.frequencySegment2Count = this.list.filter(r => r.frequencySegment === 2).reduce((a, b) => a + b.frequencyCount, 0);
      this.frequencySegment3Count = this.list.filter(r => r.frequencySegment === 3).reduce((a, b) => a + b.frequencyCount, 0);

      this.monetarySegment1Count = this.list.filter(r => r.monetarySegment === 1).reduce((a, b) => a + b.monetaryCount, 0);
      this.monetarySegment2Count = this.list.filter(r => r.monetarySegment === 2).reduce((a, b) => a + b.monetaryCount, 0);
      this.monetarySegment3Count = this.list.filter(r => r.monetarySegment === 3).reduce((a, b) => a + b.monetaryCount, 0);

      this.recency40 = this.list[0] ? isoDate(this.list[0].recency40) : '';
      this.recency90 = this.list[0] ? isoDate(this.list[0].recency90) : '';

      this.frequency40 = this.list[0] ? this.list[0].freq40 : 0;
      this.frequency90 = this.list[0] ? this.list[0].freq90 : 0;

      this.monetary33 = this.list[0] ? this.list[0].mon33 : 0;
      this.monetary66 = this.list[0] ? this.list[0].mon66 : 0;

      this.currency = this.list[0] ? this.list[0].currency : Currency.KZT;

      const segmentList = [
        {name: this.tr.translate('rfm-analysis.lost'), values: ['111', '112', '113']},
        {name: this.tr.translate('rfm-analysis.notConvinced'), values: ['121', '122', '123']},
        {name: this.tr.translate('rfm-analysis.lostRisk'), values: ['131', '132', '211']},
        {name: this.tr.translate('rfm-analysis.drifting'), values: ['133', '212', '213']},
        {name: this.tr.translate('rfm-analysis.loyal'), values: ['221', '222', '231']},
        {name: this.tr.translate('rfm-analysis.potentiallyLoyal'), values: ['223', '232', '233']},
        {name: this.tr.translate('rfm-analysis.new'), values: ['311', '312', '313']},
        {name: this.tr.translate('rfm-analysis.promising'), values: ['321', '322', '331']},
        {name: this.tr.translate('rfm-analysis.brandAmbassador'), values: ['323', '332', '333']}
      ];

      segmentList.forEach(segment => {
        const totalRfmCustomers = this.list.filter(r => segment.values.includes(r.rfmSegment)).reduce((a, b) => a + b.totalRfmCustomers, 0);
        const userMonetary = this.list.filter(r => segment.values.includes(r.rfmSegment)).reduce((a, b) => a + b.userMonetary, 0);
        const frequencyCount = this.list.filter(r => segment.values.includes(r.rfmSegment)).reduce((a, b) => a + b.frequencyCount, 0);
        this.rfmDataList.push({
          name: segment.name,
          segments: segment.values,
          clientAmount: totalRfmCustomers,
          spendMoneyTotal: userMonetary ?? 0,
          clientAvgTotal: toFixed(userMonetary / totalRfmCustomers) ?? 0,
          orderTotalAmount: frequencyCount,
          clientOrderAvgTotal: toFixed(frequencyCount / totalRfmCustomers) ?? 0
        });
      });
    } catch (err: any) {
      this.toastr.error(err.error.description, this.tr.translate('error') + ' ' + err.error.id);
    } finally {
      this.isloading.rfm = false;
    }
  }
}
